import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
} from "@mui/material";
import { useModalStore } from "../../context/modalStore";
import { usePosStore } from "../../context/posStore";
import { CommonService } from "../../services/CommonService";
import { useCustomerIdentification } from "../../hooks/useCustomerIdentification";
import { ICustomer } from "../../common/interfaces";

/**
 * Gate de identificación de cliente: se antepone a Pagar (Finish) y a
 * Carrito -> Envío (OrderOptions). Recibe por params `nextModal` y
 * `nextParams`, y al resolver el cliente salta a ese modal con el
 * cliente identificado ya incluido en los params.
 */
export const CustomerIdentification: React.FC = () => {
  const modalManager = useModalStore();
  const setCustomer = usePosStore((state) => state.setCustomer);
  const {
    step,
    phone,
    name,
    setName,
    genericName,
    setGenericName,
    found,
    matches,
    searching,
    setPhone,
    selectMatch,
    advanceToName,
    startGeneric,
    resolveCustomer,
    genericCustomer,
  } = useCustomerIdentification();
  const [submitting, setSubmitting] = useState(false);

  const nextModal = modalManager.params?.nextModal;
  const nextParams = modalManager.params?.nextParams ?? {};

  const goToNext = (customer: any) => {
    setCustomer(customer);
    // nextParams.order es una foto tomada antes de abrir este modal; si no se
    // refresca aquí, las notas escritas por handleConfirmGeneric (setOrder)
    // se pierden al pasar a Finish/OrderOptions.
    const { order } = usePosStore.getState();
    modalManager.setComponent(nextModal, { ...nextParams, order, customer });
  };

  // "Público en General": no crea/actualiza nada en la tabla clientes. El
  // nombre tecleado solo se agrega a las notas del pedido para poder
  // identificarlo, y el cliente real sigue siendo el genérico (id 1).
  const handleConfirmGeneric = () => {
    if (genericName.trim() === "") {
      CommonService.displayAlert("El nombre es requerido");
      return;
    }
    const { order, setOrder } = usePosStore.getState();
    const noteLine = `Cliente: ${genericName.trim()}`;
    setOrder({
      ...order,
      notes_order: order.notes_order ? `${noteLine}\n${order.notes_order}` : noteLine,
    });
    goToNext({ ...genericCustomer, nombre: genericName.trim() });
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && phone.length > 0) {
      advanceToName();
    }
  };

  const handleContinue = async () => {
    if (phone === "") {
      CommonService.displayAlert("Debe escribir el número del cliente");
      return;
    }
    if (name === "") {
      CommonService.displayAlert("El nombre es requerido");
      return;
    }
    setSubmitting(true);
    const customer = await resolveCustomer();
    setSubmitting(false);
    if (!customer) {
      CommonService.toast("No se pudo identificar al cliente", "error");
      return;
    }
    goToNext(customer);
  };

  return (
    <Box sx={{ mt: 1 }}>
      {step === "phone" && (
        <>
          <Typography variant="body1" gutterBottom>
            Escriba el número de teléfono del cliente
          </Typography>
          <TextField
            margin="normal"
            required
            fullWidth
            autoFocus
            id="customer-phone"
            label="Teléfono"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={handlePhoneKeyDown}
          />
          {searching && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Buscando coincidencias...</Typography>
            </Box>
          )}
          {!searching && matches.length > 0 && (
            <Paper variant="outlined" sx={{ mt: 1, maxHeight: 260, overflow: "auto" }}>
              <List dense>
                {matches.map((match: ICustomer) => (
                  <ListItemButton key={match.id} onClick={() => selectMatch(match)}>
                    <ListItemText primary={match.nombre} secondary={match.telefono} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={startGeneric}>
              Público en General / Omitir
            </Button>
            <Button
              variant="contained"
              onClick={advanceToName}
              disabled={phone.length === 0}
            >
              Continuar
            </Button>
          </Box>
        </>
      )}
      {step === "name" && (
        <>
          <Typography variant="body1" gutterBottom>
            {found ? "Cliente encontrado" : "Cliente nuevo"} — {phone}
          </Typography>
          <TextField
            margin="normal"
            required
            fullWidth
            autoFocus
            id="customer-name"
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleContinue()}
          />
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={startGeneric} disabled={submitting}>
              Público en General / Omitir
            </Button>
            <Button variant="contained" onClick={handleContinue} disabled={submitting}>
              {submitting ? <CircularProgress size={20} /> : "Continuar"}
            </Button>
          </Box>
        </>
      )}
      {step === "genericName" && (
        <>
          <Typography variant="body1" gutterBottom>
            Público en General — escriba un nombre para identificar el pedido
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Este nombre solo se guarda en las notas del pedido, no crea ni modifica ningún cliente.
          </Typography>
          <TextField
            margin="normal"
            required
            fullWidth
            autoFocus
            id="generic-name"
            label="Nombre para identificar el pedido"
            value={genericName}
            onChange={(e) => setGenericName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleConfirmGeneric()}
          />
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="contained" onClick={handleConfirmGeneric}>
              Continuar
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
